import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";

import {
  addMessage,
  conversationUpdateSuccess,
  getConversationsFailure,
  getConversationsSuccess,
  getMessagesFailure,
  getMessagesSuccess,
  getMoreConversationsFailure,
  getMoreConversationsSuccess,
  getMoreMessagesFailure,
  getMoreMessagesSuccess,
  messageReceivedUnreadCountSuccess,
  removeMessage,
  getConversations as getConversationsAction,
  deleteMessageSuccess,
  sendMessageSuccess,
  sendMessageFailure,
  createConversationSuccess,
  getConversationDetailsFailure,
  getConversationDetailsSuccess,
  markAsReadSuccess,
  // updateIsMyMessagesReadSuccess,
  markMessagesInConversationRead,
  updateConversation as updateConversationById,
  getConversations as getMyConversations,
  markAsRead,
  removeMessageFromConversation,
  getUnreadCount as getUnreadConversationsCount,
  getUnreadCountFailure,
  getUnreadCountSuccess,
  editMessageFailure,
  editMessageSuccess,
  messageEditedSuccess,
  newConversationReceivedSuccess,
  messageReceivedSuccess,
} from "./actions";
import {
  conversationUpdateActionTypes,
  createConversationActionTypes,
  deleteMessageActionTypes,
  editMessageActionTypes,
  getConversationDetailsActionTypes,
  getConversationsActionTypes,
  getMessagesActionTypes,
  getMoreConversationsActionTypes,
  getMoreMessagesActionTypes,
  getUnreadCountActionTypes,
  hideConversationActionTypes,
  markAsReadActionTypes,
  messageDeletedActionTypes,
  messageEditedActionTypes,
  messageReceivedActionTypes,
  messageReceivedUnreadCountActionTypes,
  newConversationReceivedActionTypes,
  sendMessageActionTypes,
  updateIsMyMessagesReadActionTypes,
} from "./actionTypes";
import {
  blockedByUserSuccess,
  setAuthors,
  unblockedByUserSuccess,
} from "../authors/actions";
import { CONTEXT } from "@const/redux";
import {
  normalizeConversation,
  normalizeConversationsArray,
  normalizeMessage,
  normalizeMessages,
} from "@helpers/conversations";
import {
  blockedByUserActionTypes,
  unblockedByUserActionTypes,
} from "../authors/actionTypes";
import { setConversation } from "../creatorProfile/actions";
import { setPosts, updatePost } from "../posts/actions";
import { limit } from "@const/chat";

const getConversationDetailsAsync = ({
  ConversationsService,
  conversationId,
}) => {
  if (!ConversationsService) {
    console.log("Required service does not exist");
    return;
  }

  return ConversationsService.getConversation(conversationId);
};

function* getConversationDetails({ payload }) {
  try {
    const { ConversationsService } = yield getContext(CONTEXT.SERVICES);
    const { data } = yield call(getConversationDetailsAsync, {
      ConversationsService,
      ...payload,
    });

    const { entities, result } = normalizeConversation(data);
    const { conversationOpponents, conversations } = entities;

    yield all([
      put(setAuthors(conversationOpponents)),
      put(getConversationDetailsSuccess(conversations[result])),
    ]);
  } catch (error) {
    console.error(error);
    yield put(getConversationDetailsFailure());
  }
}

const getConversationsAsync = ({ ConversationsService, payload }) => {
  if (!ConversationsService) {
    console.log("Required services does not exist");
    return;
  }

  return ConversationsService.getConversations(payload);
};

function* getConversations({ payload }) {
  try {
    const { ConversationsService } = yield getContext(CONTEXT.SERVICES);
    const {
      data,
      pagination: { totalCount, nextOffset },
    } = yield call(getConversationsAsync, {
      ConversationsService,
      payload: { limit: payload.limit },
    });

    const {
      entities: {
        messages,
        conversationOpponents,
        conversations: conversationsById,
      },
      result: allConversations,
    } = normalizeConversationsArray(data);

    const {
      entities: { messages: messagesById },
      result: allMessages,
    } = normalizeMessages(messages);

    yield all([
      put(setAuthors(conversationOpponents)),
      put(
        getConversationsSuccess({
          allConversations,
          allMessages,
          messagesById,
          conversationsById,
          totalCount,
          nextOffset,
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getConversationsFailure(error));
  }
}

function* getMoreConversations({ payload }) {
  try {
    const { ConversationsService } = yield getContext(CONTEXT.SERVICES);
    const {
      data,
      pagination: { totalCount, nextOffset },
    } = yield call(getConversationsAsync, {
      ConversationsService,
      payload,
    });

    const {
      entities: {
        conversationOpponents,
        conversations: conversationsById,
        messages,
      },
      result: allConversations,
    } = normalizeConversationsArray(data);

    const {
      entities: { messages: messagesById },
      result: allMessages,
    } = normalizeMessages(messages);

    yield all([
      put(setAuthors(conversationOpponents)),
      put(
        getMoreConversationsSuccess({
          allConversations,
          allMessages,
          messagesById,
          conversationsById,
          totalCount,
          nextOffset,
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getMoreConversationsFailure(error));
  }
}

const getMessagesAsync = ({ ConversationsService, id, ...params }) => {
  if (!ConversationsService) {
    console.log("Required services does not exist");
    return;
  }

  return ConversationsService.getConversationMessages(id, params);
};

function* getMessages({ payload }) {
  try {
    const { ConversationsService } = yield getContext(CONTEXT.SERVICES);

    const {
      data,
      pagination: { totalCount: totalMessages },
    } = yield call(getMessagesAsync, {
      ConversationsService,
      ...payload,
    });

    const {
      result: allMessages,
      entities: { messages: messagesById, post },
    } = normalizeMessages(data);
    yield all([
      put(setPosts({ postById: post })),
      put(
        getMessagesSuccess({
          allMessages,
          messagesById,
          totalMessages,
          id: payload.id,
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getMessagesFailure());
  }
}

function* getMoreMessages({ payload }) {
  try {
    const { ConversationsService } = yield getContext(CONTEXT.SERVICES);

    const {
      data,
      pagination: { totalCount: totalMessages },
    } = yield call(getMessagesAsync, {
      ConversationsService,
      ...payload,
    });

    const {
      result: allMessages,
      entities: { messages: messagesById, post },
    } = normalizeMessages(data);

    yield all([
      put(setPosts({ postById: post })),
      put(
        getMoreMessagesSuccess({
          allMessages,
          messagesById,
          totalMessages,
          id: payload.id,
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getMoreMessagesFailure());
  }
}

const hideConversationAsync = ({ ConversationsService, id }) => {
  return ConversationsService.hideConversation(id);
};

function* hideConversation({ payload }) {
  try {
    const { id } = payload;
    const { ConversationsService } = yield getContext(CONTEXT.SERVICES);
    yield call(hideConversationAsync, {
      id,
      ConversationsService,
    });
  } catch ({ error }) {
    console.log(error);
  }
}

function* updateConversation({ payload }) {
  const { data } = payload;

  const { entities, result } = normalizeConversation(data);
  const { conversationOpponents, conversations, messages } = entities;
  const {
    entities: { messages: messagesById, post },
    result: allMessages,
  } = normalizeMessages(messages);

  const { id: currentChatId } = yield select(
    ({ conversationsReducer }) => conversationsReducer,
  );

  if (currentChatId == result) {
    yield put(markAsRead({ conversationId: result }));
  }

  yield all([
    put(setAuthors(conversationOpponents)),
    put(setPosts({ postById: post })),
    put(addMessage({ allMessages, messagesById, conversationId: result })),
    put(
      conversationUpdateSuccess({
        conversations,
        conversationId: result,
      }),
    ),
  ]);
}

// todo: useless. remove
function* receiveMessage({ payload }) {
  const { data } = payload;
  yield put(messageReceivedSuccess(data));
}

const deleteMessageAcync = ({ MessagesService, id }) => {
  return MessagesService.deleteMessage(id);
};

function* deleteMessage({ payload }) {
  try {
    const { id, conversationId, uuid } = payload;
    const { MessagesService } = yield getContext(CONTEXT.SERVICES);

    yield call(deleteMessageAcync, {
      MessagesService,
      id,
    });

    yield put(deleteMessageSuccess({ id, conversationId, uuid }));
  } catch ({ error }) {
    console.log("deleteMessage::", error);
  }
}

function* updateMessagesCounter({ payload }) {
  const {
    data: conversation,
    unreadConversationCount,
    deletedMessageId,
    shouldUpdateList,
  } = payload;

  if (deletedMessageId) {
    const { entities, result } = normalizeConversation(conversation);
    const { conversationOpponents, conversations, messages } = entities;
    const {
      entities: { messages: messagesById },
    } = normalizeMessages(messages);

    if (shouldUpdateList) {
      const { totalCount = 0 } = yield select(
        ({ conversationsReducer }) => conversationsReducer,
      );

      if (totalCount) {
        yield put(getConversationsAction({ limit: totalCount }));
      }
    }

    yield all([
      put(setAuthors(conversationOpponents)),
      put(
        removeMessage({
          messagesById,
          deletedMessageId,
          conversationId: result,
        }),
      ),
      put(
        conversationUpdateSuccess({
          conversations,
          conversationId: result,
        }),
      ),
    ]);
  }

  // todo: if any bugs -> check backend if value missing
  yield put(messageReceivedUnreadCountSuccess(unreadConversationCount));
}

function* unblockedByUser({ payload }) {
  try {
    const {
      data: { emitterId },
    } = payload;

    yield put(unblockedByUserSuccess(emitterId));
  } catch (error) {
    console.log(error);
  }
}

function* blockedByUser({ payload }) {
  try {
    const {
      data: { emitterId },
    } = payload;

    yield put(blockedByUserSuccess(emitterId));
  } catch (error) {
    console.log(error);
  }
}

const sendMessageAsync = ({ ConversationsService, id, text, uuid }) => {
  if (!ConversationsService) {
    console.log("Missing required service");
    return;
  }

  return ConversationsService.sendMessage(id, { text, uuid });
};

function* sendMessage({ payload }) {
  const { ConversationsService, ToasterService } = yield getContext(
    CONTEXT.SERVICES,
  );

  const { messagesByChat } = yield select(
    ({ conversationsReducer }) => conversationsReducer,
  );

  const chatMessagesIds = messagesByChat[+payload.id];

  try {
    const { data: message } = yield call(sendMessageAsync, {
      ConversationsService,
      ...payload,
    });
    if (!chatMessagesIds.length) {
      yield put(getConversationsAction({ limit }));
    }
    yield put(
      sendMessageSuccess({
        message,
        conversationId: payload.id,
        uuid: payload.uuid,
      }),
    );
  } catch (e) {
    yield put(sendMessageFailure({ uuid: payload.uuid }));
    if (
      payload.deletedAccountCallback &&
      e?.error?.message === "USER_IS_DELETED"
    ) {
      payload.deletedAccountCallback();
    } else {
      ToasterService.errorTranslated("MSG_WAS_NOT_SENT");
    }
  }
}

const createConversationAsync = ({ UsersService, opponentId }) => {
  if (!UsersService) {
    console.log("Missing required service");
    return;
  }

  return UsersService.createConversation(opponentId);
};

function* createConversation({ payload }) {
  try {
    const { UsersService } = yield getContext(CONTEXT.SERVICES);
    const { data } = yield call(createConversationAsync, {
      UsersService,
      ...payload,
    });

    const { entities, result } = normalizeConversation(data);
    const { conversationOpponents, conversations } = entities;

    yield all([
      put(setAuthors(conversationOpponents)),
      put(
        createConversationSuccess({
          result,
          conversations,
        }),
      ),
      put(
        setConversation({
          conversationId: result,
          opponentId: payload.opponentId,
        }),
      ),
    ]);

    console.log(data);
  } catch (e) {
    console.log(e);
  }
}

const readConvesationAsync = ({ ConversationsService, conversationId }) => {
  if (!ConversationsService) {
    console.log("Required service is missing");
    return;
  }

  return ConversationsService.readConversation(conversationId);
};

function* readConvesation({ payload }) {
  try {
    const { ConversationsService } = yield getContext(CONTEXT.SERVICES);
    yield call(readConvesationAsync, { ConversationsService, ...payload });

    const { id: myId } = yield select(({ profileReducer }) => profileReducer);

    yield put(markAsReadSuccess({ ...payload, myId }));
    yield put(getUnreadConversationsCount());
  } catch (error) {
    console.log(error);
  }
}

function* setMyMessagesRead({ payload }) {
  try {
    const { data } = payload;

    const { entities, result } = normalizeConversation(data);
    const { conversationOpponents, conversations } = entities;

    yield all([
      put(setAuthors(conversationOpponents)),
      put(
        updateConversationById({ result, conversation: conversations[result] }),
      ),
      put(markMessagesInConversationRead(result)),
    ]);
  } catch (error) {
    console.log(error);
  }
}

function* messageDeleted({ payload }) {
  try {
    const { data, deletedMessageId, shouldUpdateList } = payload;

    const { result, entities } = normalizeConversation(data);
    const { conversationOpponents, conversations, messages } = entities;

    const {
      entities: { messages: messagesById, post },
    } = normalizeMessages(messages);

    yield all([
      put(setAuthors(conversationOpponents)),
      put(addMessage({ messagesById, allMessages: [] })),
      put(setPosts({ postById: post })),
      put(
        removeMessageFromConversation({
          conversationId: result,
          messageId: deletedMessageId,
        }),
      ),
      put(
        updateConversationById({ result, conversation: conversations[result] }),
      ),
    ]);

    if (shouldUpdateList) {
      const { allConversations } = yield select(
        ({ conversationsReducer }) => conversationsReducer,
      );

      yield put(getMyConversations({ limit: allConversations?.length }));
    }
  } catch (e) {
    console.error(e);
  }
}

const getUnreadCountAsync = ({ ConversationsService }) => {
  if (!ConversationsService) {
    console.log("Missing required service");
    return;
  }

  return ConversationsService.getUnreadCount();
};

function* getUnreadCount() {
  try {
    const { ConversationsService } = yield getContext(CONTEXT.SERVICES);
    const { data } = yield call(getUnreadCountAsync, {
      ConversationsService,
    });

    yield put(getUnreadCountSuccess(data));
  } catch (e) {
    yield put(getUnreadCountFailure(e));
  }
}

const editMessageAsync = ({ MessagesService, id, ...body }) => {
  if (!MessagesService) {
    console.log("Missing required service");
    return;
  }

  return MessagesService.editMessage(id, body);
};

function* editMessage({ payload }) {
  const { id, text } = payload;

  try {
    const { MessagesService } = yield getContext(CONTEXT.SERVICES);
    const { data } = yield call(editMessageAsync, {
      MessagesService,
      id,
      text,
    });

    const {
      entities: { messages },
      result,
    } = normalizeMessage(data);

    yield put(editMessageSuccess(messages[result]));
  } catch ({ error }) {
    yield put(editMessageFailure(id));
  }
}

function* messagEdited({ payload }) {
  try {
    const { data } = payload;

    const {
      entities: { messages, post },
      result,
    } = normalizeMessage(data);

    if (post) {
      yield put(
        updatePost({
          id: Object.values(post)[0].id,
          post: Object.values(post)[0],
        }),
      );
    }

    yield put(messageEditedSuccess(messages[result]));
  } catch (error) {
    console.error(error);
  }
}

function* newConversationReceived({ payload }) {
  try {
    const { data } = payload;

    const { entities, result } = normalizeConversation(data);

    const { conversationOpponents, conversations, messages } = entities;
    const {
      entities: { messages: messagesById },
      result: allMessages,
    } = normalizeMessages(messages);

    yield all([
      put(setAuthors(conversationOpponents)),
      put(newConversationReceivedSuccess(conversations[result])),
      put(addMessage({ allMessages, messagesById, conversationId: result })),
    ]);
  } catch (error) {
    console.error("newConversationReceived::error", error);
  }
}

export function* watchGetMessages() {
  yield takeLatest(getMessagesActionTypes.request, getMessages);
}

export function* watchMoreGetMessages() {
  yield takeLatest(getMoreMessagesActionTypes.request, getMoreMessages);
}

export function* watchHideConversation() {
  yield takeLatest(hideConversationActionTypes.request, hideConversation);
}

export function* watchUpdateConversation() {
  yield takeEvery(conversationUpdateActionTypes.request, updateConversation);
}

export function* watchReceiveMessage() {
  yield takeEvery(messageReceivedActionTypes.request, receiveMessage);
}

export function* watchDeleteMessage() {
  yield takeEvery(deleteMessageActionTypes.request, deleteMessage);
}

export function* watchUpdateMessagesCounter() {
  yield takeEvery(
    messageReceivedUnreadCountActionTypes.request,
    updateMessagesCounter,
  );
}

export function* watchUnblockedByUser() {
  yield takeLatest(unblockedByUserActionTypes.request, unblockedByUser);
}

export function* watchBlockedByUser() {
  yield takeEvery(blockedByUserActionTypes.request, blockedByUser);
}

export function* watchSendMessage() {
  yield takeEvery(sendMessageActionTypes.request, sendMessage);
}

export function* watchCreateConversation() {
  yield takeEvery(createConversationActionTypes.request, createConversation);
}

export function* watchGetConversationDetails() {
  yield takeLatest(
    getConversationDetailsActionTypes.request,
    getConversationDetails,
  );
}

export function* watchGetConversations() {
  yield takeLatest(getConversationsActionTypes.request, getConversations);
}

export function* watchGetMoreConversations() {
  yield takeEvery(
    getMoreConversationsActionTypes.request,
    getMoreConversations,
  );
}

export function* watchReadConversation() {
  yield takeEvery(markAsReadActionTypes.request, readConvesation);
}

export function* watchMyConversationRead() {
  yield takeEvery(updateIsMyMessagesReadActionTypes.request, setMyMessagesRead);
}

export function* watchMessageDeleted() {
  yield takeEvery(messageDeletedActionTypes.request, messageDeleted);
}

export function* watchGetUnreadCount() {
  yield takeEvery(getUnreadCountActionTypes.request, getUnreadCount);
}

export function* wathcEditMessage() {
  yield takeEvery(editMessageActionTypes.request, editMessage);
}

export function* wathcMessageEdited() {
  yield takeEvery(messageEditedActionTypes.request, messagEdited);
}

export function* watchNewConversationReceived() {
  yield takeEvery(
    newConversationReceivedActionTypes.request,
    newConversationReceived,
  );
}
